import { useEffect, useState, MouseEvent } from "react"
import { useGetResultsetsCountResultsetCountGet, useGetResultsetsResultsetGet, useGetResultsetTagsResultsetTagsGet } from "../../../api/generated/fastAPI"
import { GetResultsetsResultsetGetParams, Resultset } from "../../../api/models"
import FilterBar, { FilterBarType } from "../../../components/Filtering/FilterBar"
import { TableData, TableHeader } from "../../../components/Table/DataTable"
import useDataParams, { GetParamsTypes } from "../../../hooks/useDataParams"
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown"
import { useNavigate } from "react-router-dom"
import DeleteModal, { DeleteItemType } from "../../../components/Popups/DeleteModal"
import SearchBar from "../../../components/Filtering/SearchBar"
import ColumnsButton from "../../../components/Buttons/ColumnsButton"
import UpdatedPagination from "../../../components/Filtering/UpdatedPagination"
import { exportFileExtensions } from "../../../utils/supplementalData"
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown"
import useDelete from "../../../hooks/endpoints/useDelete"
import useDownload from "../../../hooks/endpoints/useDownload"
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable"

const resultsetHeaders: TableHeader[] = [
    {
        label: 'Name',
        value: 'name',
        isShown: true,
        alignment: 'left',
    },
    {
        label: 'Tags',
        value: 'tags',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Created By',
        value: 'created_by',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Owned By',
        value: 'owner',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Created',
        value: 'creation_date',
        isShown: true,
        alignment: 'center',
    },
    {
        label: 'Date Updated',
        value: 'updated_date',
        isShown: true,
        alignment: 'center',
    },
]

const defaultParams: GetResultsetsResultsetGetParams = {
    name: null,
    created_by: null,
    owner: null,
    start_date: null,
    end_date: null,
    tag_groups: null,
    sort_by: 'creation_date',
    sort_order: 'desc',
    limit: 10,
    offset: 0,
}

const defaultFilters: FilterBarType[] = [
    {
        label: 'Created Date Range',
        param: 'start_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Created Date Range',
        param: 'end_date',
        type: 'date-range',
        isDefault: true,
        groupKey: 'date-range',
    },
    {
        label: 'Tags',
        param: 'tag_groups',
        type: 'tags',
        isDefault: true,
    },
    {
        label: 'Created By',
        param: 'created_by',
        type: 'users',
        isDefault: true,
    },
    {
        label: 'Owner',
        param: 'owner',
        type: 'users',
        isDefault: true,
    },
]

export default function BatchPredictions({
    setPopupMessage,
}: {
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
}) {
    const getResultsetTags = useGetResultsetTagsResultsetTagsGet();
    const { downloadBatchPrediction } = useDownload();
    
    const {
        params,
        setParams,
        search,
        sort,
        filters,
        setPaginationLimit,
        setPaginationOffset
    } = useDataParams({
        defaultParams,
        defaultFilters,
        tagsData: getResultsetTags.data as unknown as { [key: string]: string[] },
    })

    const getResultsetsMutation = useGetResultsetsResultsetGet(params as GetParamsTypes);
    const getResultsetCountMutation = useGetResultsetsCountResultsetCountGet({
        name: (params as GetParamsTypes).name!,
        created_by: (params as GetParamsTypes).created_by!,
        owner: (params as GetParamsTypes).owner!,
        start_date: (params as GetParamsTypes).start_date!,
        end_date: (params as GetParamsTypes).end_date!,
        tag_groups: (params as GetParamsTypes).tag_groups!,
    });
    const [ count, setCount ] = useState(0);

    const [ resultsets, setResultsets ] = useState<TableData[]>([]);
    const [ allResultsets, setAllResultsets ] = useState<Resultset[]>([]);
    const [ selectedItems, setSelectedItems ] = useState<(string | number)[]>([]);
    const [ tableHeaders, setTableHeaders ] = useState<TableHeader[]>(resultsetHeaders);
    const [ actionDropdownOptions, setActionDropdownOptions ] = useState<DropdownOption[]>([]);

    const navigate = useNavigate();

    const refetchBatchPredictions = () => {
        getResultsetsMutation.refetch();
        getResultsetCountMutation.refetch();
    }

    const { deleteResultsets } = useDelete({ setPopupMessage, refetchFn: refetchBatchPredictions });
    
    useEffect(() => {
        if (getResultsetsMutation.data) {
            const resultsetData = getResultsetsMutation.data as unknown as Resultset[];
            setAllResultsets(resultsetData);

            const items: TableData[] = resultsetData.map(result => ({
                id: result.id!.toString(),
                fields: [
                    {
                        associatedColumn: 'name',
                        value: result.name ? result.name : 'No value',
                        alignment: 'left' as any,
                        type: 'text' as any,
                        action: (e:MouseEvent<HTMLButtonElement>) => navigate(`/resultsets/${result.id!}`),
                    },
                    {
                        associatedColumn: 'tags',
                        value: result.resultset_metadata!.tags! as string[],
                        alignment: 'center' as any,
                        type: 'tags' as any,
                    },
                    {
                        associatedColumn: 'created_by',
                        value: result.created_by ? result.created_by : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                    {
                        associatedColumn: 'owner',
                        value: result.owner ? result.owner : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                    {
                        associatedColumn: 'creation_date',
                        value: result.creation_date ? new Date(result.creation_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                    {
                        associatedColumn: 'updated_date',
                        value: result.updated_date ? new Date(result.updated_date!).toLocaleString('en-US', {timeZone: 'EST'}) : 'No value',
                        alignment: 'center' as any,
                        type: 'text' as any,
                    },
                ],
                options: injectOptions(result),
            }))

            setResultsets(items);
        }
        //eslint-disable-next-line
    }, [getResultsetsMutation.data]);

    useEffect(() => {
        if (getResultsetCountMutation.data) {
            const data = getResultsetCountMutation.data as unknown as number;
            setCount(data);
        }
    }, [getResultsetCountMutation.data]);

    const injectOptions = (resultset: Resultset) => {
        const id = resultset.id!;
        const name = resultset.name!;

        return [
            {
                label: 'Export',
                action: (e:MouseEvent<HTMLButtonElement>) => {},
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            try {
                                await downloadBatchPrediction(
                                    ext.file_type,
                                    resultset,
                                );
    
                                setPopupMessage('You have successfully downloaded the selected resultset.', true);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading this resultset. Please try again.', false);
                            }
                        }
                    }
                ))
            },
            {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={[{
                            type: 'resultset',
                            id,
                            name,
                        }]}
                        deleteFn={deleteResultsets}
                    />
                )
            },
        ]
    };

    useEffect(() => {
        let options: DropdownOption[] = [];

        if (allResultsets.length > 0 && selectedItems.length > 0) {
            let items: DeleteItemType[] = [];
            let exportItems: Resultset[] = [];
            console.log(selectedItems, allResultsets)
            selectedItems.forEach(id => {
                const matchingItem = allResultsets.find(rs => rs.id === Number(id));
                if (matchingItem) {
                    items.push({
                        type: 'resultset',
                        id,
                        name: matchingItem.name ? matchingItem.name : ''
                    });

                    exportItems.push(matchingItem);
                }
            });

            const deleteModalComp: DropdownOption = {
                label: 'Delete',
                btnComp: (
                    <DeleteModal 
                        items={items}
                        cancelFn={() => setSelectedItems([])}
                        deleteFn={deleteResultsets}
                    />
                ),
            };

            const exportModalComp: DropdownOption = {
                label: 'Export',
                moreOptions: exportFileExtensions.map(ext => (
                    {
                        label: ext.label,
                        action: async (e: MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            try {
                                const promises = exportItems.map(async(item) => {
                                    return downloadBatchPrediction(
                                        ext.file_type,
                                        item,
                                    )
                                });
                                
                                await Promise.all(promises);
    
                                setPopupMessage('You have successfully downloaded the selected resultsets.', true);
                                setSelectedItems([]);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading the selected resultsets. Please try again.', false);
                            }
                        }
                    }
                ))
            };

            options.push(exportModalComp);
            options.push(deleteModalComp);
        };

        setActionDropdownOptions(options);

        //eslint-disable-next-line
    }, [selectedItems, allResultsets]);


    return (
        <>
            <div className="flex flex-col justify-start items-start gap-2">
                <div className="w-full flex justify-between items-center gap-4">
                    <div className="max-w-[600px] w-full">
                        <SearchBar
                            searchFn={search}
                            searchKey="name"
                        />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <ColumnsButton 
                            headers={tableHeaders} 
                            setHeaders={setTableHeaders} 
                            isDisabled={false}
                        />
                        {selectedItems.length > 0 && (
                            <NestedDropdown
                                dir="right" 
                                btn={{
                                    label: `Actions (${selectedItems.length} selected)`,
                                    type: 'secondary',
                                    styling: 'solid',
                                    id: 'resultset-multi-action-btn',
                                }}
                                options={actionDropdownOptions}
                            />
                        )}
                    </div>
                </div>
                <FilterBar 
                    filters={filters}
                    setParams={setParams}
                    params={params}
                />
            </div>

            <VirtualizedDataTable 
                paramValues={Object.entries(params).filter(entry => ['name', 'start_date', 'end_date'].includes(entry[0]) && entry[1]).map(val => val[1])}
                isLoading={getResultsetsMutation.isLoading} 
                headers={tableHeaders} 
                tableData={resultsets}
                selection={{
                    selectedItems,
                    setSelectedItems
                }}
                sorting={{
                    sortableHeaders: ['name', 'created_by', 'owner', 'creation_date', 'updated_date'],
                    sortHeaders: sort,
                    sortByParam: params.sort_by!,
                    sortOrderParam: params.sort_order!,
                }}
                setPopupMessage={setPopupMessage}
                fixedItemHeight={72}
                count={getResultsetCountMutation.data as unknown as number | undefined}
                dataType="batch-predictions"
            />

            <UpdatedPagination 
                count={count}
                limit={params.limit!}
                offset={params.offset!}
                setPaginationLimit={setPaginationLimit}
                setPaginationOffset={setPaginationOffset}
                size="reg"
                isLoading={getResultsetCountMutation.isLoading}
            />
        </>
    )
}